import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit, ExternalLink } from 'lucide-react';
import { BookEditModal } from './BookEditModal';
import { resolveFileUrl } from '../../pages/Books';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FileBadge = ({ label, url, testId }) =>
  url ? (
    <a
      href={resolveFileUrl(url)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 hover:bg-emerald-100"
      data-testid={testId}
    >
      {label} <ExternalLink className="h-3 w-3" />
    </a>
  ) : (
    <span className="text-xs font-medium text-slate-400 bg-slate-50 border border-slate-200 rounded px-2 py-0.5" data-testid={testId}>
      No {label}
    </span>
  );

export const BooksManager = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/books`);
      setBooks(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBooks(); }, []);

  return (
    <div data-testid="books-manager">
      <h2 className="font-heading text-2xl font-medium text-brand-primary mb-2">Manage Books</h2>
      <p className="font-body text-sm text-slate-600 mb-6">
        Replace a cover, PDF or eBook (ePub) for any book on the "Our Books" page. Changes appear instantly.
      </p>
      {loading ? (
        <p className="font-body text-slate-600">Loading...</p>
      ) : (
        <div className="space-y-3" data-testid="admin-books-list">
          {books.map((book) => (
            <div key={book.id} className="bg-white border border-slate-200 rounded shadow-sm p-4 flex items-center gap-4" data-testid={`admin-book-row-${book.order}`}>
              <div className="w-14 h-20 flex-shrink-0 bg-slate-100 rounded overflow-hidden">
                {book.cover_url && <img src={resolveFileUrl(book.cover_url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-grow min-w-0">
                <p className="text-xs font-semibold text-brand-accent tracking-widest">BOOK {String(book.order).padStart(2, '0')}</p>
                <p className="font-heading text-base font-medium text-brand-primary truncate">{book.title}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <FileBadge label="Cover" url={book.cover_url} testId={`admin-book-cover-status-${book.order}`} />
                  <FileBadge label="PDF" url={book.pdf_url} testId={`admin-book-pdf-status-${book.order}`} />
                  <FileBadge label="ePub" url={book.epub_url} testId={`admin-book-epub-status-${book.order}`} />
                </div>
              </div>
              <button
                onClick={() => setEditing(book)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-body font-medium text-brand-primary border border-slate-300 rounded hover:border-brand-accent hover:text-brand-accent transition-colors duration-300"
                data-testid={`edit-book-${book.order}`}
              >
                <Edit className="h-4 w-4" /> Edit
              </button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <BookEditModal
          book={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchBooks(); }}
        />
      )}
    </div>
  );
};
